import { Box, CircularProgress } from '@mui/material'
import { useEffect, useRef } from 'react'

interface InfiniteScrollLoaderProps {
  hasMoreItems: boolean
  onReveal: () => void
  threshold?: number
}

export const InfiniteScrollLoader: React.FC<InfiniteScrollLoaderProps> = ({ hasMoreItems, onReveal, threshold = 1 }) => {
  const loaderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!hasMoreItems) return
    // Creates an observer that watches when elements enter/exit the viewport
    const observer = new IntersectionObserver(
      (entries) => {
        // Callback function that runs when visibility changes
        if (entries[0] && entries[0].isIntersecting && hasMoreItems) {
          onReveal()
        }
      },
      // Only trigger when 100% of loader is visible
      { threshold }
    )

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    // Cleanup: stop observing when effect re-runs or component unmounts
    return () => observer.disconnect()
  }, [hasMoreItems, onReveal, threshold])

  if (!hasMoreItems) return null

  return (
    <Box
      ref={loaderRef}
      sx={{
        height: 20,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <CircularProgress size={20} />
    </Box>
  )
}
